import { isPlatformServer } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	Inject,
	Input,
	OnInit,
	Optional,
	PLATFORM_ID,
	SecurityContext,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
	AppSettingsService,
	CanonicalInfo,
	CustomWindow,
	MetaDataService,
	slugify,
	WINDOW,
	REQUEST,
} from '@woolworthsnz/styleguide';
import { IngredientProduct, RecipeResponse } from '@woolworthsnz/trader-api';
import { Request } from 'express';
import { mealtimeSlug } from '../../helpers/mealtime.helper';

@Component({
	selector: 'recipe-seo',
	template: ` <section [innerHTML]="generateJsonLdSchema(recipe)"></section>`,
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
})
export class SeoComponent implements OnInit {
	@Input() recipe: RecipeResponse | undefined;

	constructor(
		private metaDataService: MetaDataService,
		private appSettings: AppSettingsService,
		private sanitizer: DomSanitizer,
		@Inject(WINDOW) private window: CustomWindow,
		@Optional() @Inject(REQUEST) private originalRequest: Request,
		@Inject(PLATFORM_ID) private platformId: Object
	) {}

	ngOnInit(): void {
		this.setPageMeta(this.recipe);
	}

	public generateJsonLdSchema(recipe?: RecipeResponse): string | SafeHtml {
		if (!recipe) {
			return ``;
		}

		const schema = this.mapRecipeToJsonSchema(recipe);

		return this.sanitizer.bypassSecurityTrustHtml(
			`<script type="application/ld+json">${JSON.stringify(schema, null, 2)}</script>`
		);
	}

	private setPageMeta(recipe: RecipeResponse | undefined): void {
		const settingKey = 'recipeDetail';
		const defaultSettingKey = 'default';
		const title = recipe?.title;
		const mealtime = mealtimeSlug(recipe?.mealtimes?.split(',') ?? []);
		const canonicalInfo: CanonicalInfo | undefined = recipe?.canonicalLink
			? { productName: recipe.title ?? '', canonicalLink: recipe?.canonicalLink }
			: undefined;

		this.metaDataService.setPageMeta(
			title
				? this.appSettings.getPageTitle(settingKey, this.seoOptimise(title))
				: this.appSettings.getPageTitle(defaultSettingKey),
			title ? this.appSettings.getMetaTitle(settingKey, title) : this.appSettings.getMetaTitle(defaultSettingKey),
			title
				? this.appSettings.getMetaDescription(settingKey, title)
				: this.appSettings.getMetaDescription(defaultSettingKey),
			recipe?.largeImageFilename
				? `${this.appSettings.getMetaUrl(defaultSettingKey)}${recipe.largeImageFilename}`
				: this.appSettings.getMetaImage(defaultSettingKey),
			recipe?.id
				? `${this.appSettings.getMetaUrl(defaultSettingKey)}/recipes/${mealtime}/${recipe.id}/${slugify(
						recipe.title
				  )}`
				: this.appSettings.getMetaUrl(defaultSettingKey),
			canonicalInfo
		);
	}

	private seoOptimise(title: string): string {
		return title.replace(' and ', ' & ');
	}

	private mapRecipeToJsonSchema(recipe: RecipeResponse): any {
		const totalCookTime = this.getRecipeTotalTime(recipe.cookingDuration, recipe.prepDuration);
		const keywords = [
			...new Set([
				...this.splitAndFormat(recipe.tags),
				...this.splitAndFormat(recipe.nutritionTags),
				...this.splitAndFormat(recipe.seasons),
			]),
		];

		/* eslint-disable  @typescript-eslint/naming-convention */
		return {
			'@context': 'https://schema.org/',
			'@type': 'Recipe',
			name: recipe.title,
			image: [
				recipe.largeImageFilename,
				recipe.smallImageFilename,
				recipe.thumbnailImageFilename,
				recipe.relatedImageFilename,
			]
				.filter((url) => !!url)
				.map((url) => this.sanitizer.sanitize(SecurityContext.URL, this.generateBaseUrlIfRequired(url) ?? '')),
			author: recipe.contributorName,
			description: recipe.description,
			datePublished: recipe.dateValidFrom,
			prepTime: recipe.prepDuration ? `PT${recipe.prepDuration}M` : undefined,
			cookTime: recipe.cookingDuration ? `PT${recipe.cookingDuration}M` : undefined,
			totalTime: totalCookTime ? `PT${totalCookTime}M` : undefined,
			keywords: keywords.join(', '),
			recipeYield: recipe.servings,
			recipeIngredient: this.getDistinctIngredients(recipe.ingredients),
			recipeInstructions: recipe.instructions,
			recipeCategory: mealtimeSlug(recipe.mealtimes?.split(',') || []).replace('-', ' '),
		};
		/* eslint-enable  @typescript-eslint/naming-convention */
	}

	private getRecipeTotalTime(cookingDuration?: number, prepDuration?: number): number | undefined {
		if (cookingDuration || prepDuration) {
			return (cookingDuration || 0) + (prepDuration || 0);
		}

		return undefined;
	}

	private getDistinctIngredients(ingredients: IngredientProduct[] | undefined): string[] | undefined {
		if (!ingredients) {
			return undefined;
		}

		// get unique list of ingredient descriptions and filter out headings
		return [
			...new Set(
				ingredients
					.filter((ingredient) => ingredient.ingredientType !== 'Heading')
					.map((ingredient) => {
						// append measurements if available
						const measurements = [ingredient.measurementValue, ingredient.measurementType]
							.filter((i) => !!i?.trim())
							.join(' ');

						if (measurements.length) {
							return `${measurements} ${ingredient.description?.trim()}`;
						}

						return ingredient.description?.trim() ?? '';
					})
			),
		];
	}

	private generateBaseUrlIfRequired(imageUrl?: string): string | undefined {
		if (imageUrl && !imageUrl.toLowerCase().startsWith('http')) {
			if (isPlatformServer(this.platformId)) {
				return `${this.originalRequest.url.substr(0, this.originalRequest.url.lastIndexOf('/'))}${imageUrl}`;
			} else {
				return `${this.window.location.origin}${imageUrl}`;
			}
		}

		return imageUrl;
	}

	private splitAndFormat(val: string | undefined): string[] {
		if (!val) {
			return [];
		}

		return val
			.split(',')
			.map((item) => item?.trim().toLocaleLowerCase())
			.filter((item) => !!item);
	}
}
